import { useState } from 'react'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import {
    TenantMessage,
    PaginatedMessages,
    Reply,
    UnreadCountResponse,
} from '../types/tenantMessage'

export interface MessageFilters {
    search?: string
    is_read?: boolean
}

export const useTenantMessages = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [messages, setMessages] = useState<TenantMessage[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [page, setPage] = useState(1)
    const [activeFilters, setActiveFilters] = useState<MessageFilters>({})

    const [replies, setReplies] = useState<Reply[]>([])
    const [repliesLoading, setRepliesLoading] = useState(false)

    const getMessages = async (
        pageNum = 1,
        filters?: MessageFilters,
    ) => {
        if (pageNum === 1) {
            setIsLoading(true)
        } else {
            setIsLoadingMore(true)
        }

        const appliedFilters = filters ?? activeFilters
        if (filters !== undefined) {
            setActiveFilters(filters)
        }

        try {
            const params: Record<string, string | number | boolean> = {
                page: pageNum,
            }
            if (appliedFilters.search) {
                params.search = appliedFilters.search
            }
            if (appliedFilters.is_read !== undefined) {
                params.is_read = appliedFilters.is_read
            }

            const res = await axiosInstance.get<PaginatedMessages>(
                'tenant-message/inbox/',
                { params },
            )
            if (pageNum === 1) {
                setMessages(res.data.results)
            } else {
                setMessages((prev) => [...prev, ...res.data.results])
            }
            setHasMore(res.data.next !== null)
            setPage(pageNum)
            return res.data
        } catch (e) {
            toast.error('Error loading messages')
            return Promise.reject(e)
        } finally {
            setIsLoading(false)
            setIsLoadingMore(false)
        }
    }

    const loadMore = async () => {
        if (!hasMore || isLoadingMore) return
        return getMessages(page + 1)
    }

    const getUnreadCount = async () => {
        try {
            const res = await axiosInstance.get<UnreadCountResponse>(
                'tenant-message/inbox/unread-count/',
            )
            setUnreadCount(res.data.unread_count)
            return res.data.unread_count
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const getMessage = async (id: number) => {
        setIsLoading(true)
        try {
            const res = await axiosInstance.get<TenantMessage>(
                `tenant-message/inbox/${id}/`,
            )
            return res.data
        } catch (e) {
            toast.error('Error loading message')
            return Promise.reject(e)
        } finally {
            setIsLoading(false)
        }
    }

    const markRead = async (id: number) => {
        try {
            await axiosInstance.post(`tenant-message/inbox/${id}/mark-read/`)
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === id ? { ...msg, is_read: true } : msg,
                ),
            )
            setUnreadCount((prev) => Math.max(0, prev - 1))
        } catch (e) {
            toast.error('Error marking message as read')
            return Promise.reject(e)
        }
    }

    const getReplies = async (messageId: number) => {
        setRepliesLoading(true)
        try {
            const res = await axiosInstance.get<Reply[]>(
                `tenant-message/inbox/${messageId}/replies/`,
            )
            setReplies(res.data)
            return res.data
        } catch (e) {
            toast.error('Error loading replies')
            return Promise.reject(e)
        } finally {
            setRepliesLoading(false)
        }
    }

    const sendReply = async (messageId: number, body: string) => {
        try {
            const res = await axiosInstance.post<Reply>(
                `tenant-message/inbox/${messageId}/reply/`,
                { body },
            )
            setReplies((prev) => [...prev, res.data])
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === messageId
                        ? {
                              ...msg,
                              reply_count: msg.reply_count + 1,
                              latest_reply_at: res.data.created_date,
                          }
                        : msg,
                ),
            )
            return res.data
        } catch (e) {
            toast.error('Error sending reply')
            return Promise.reject(e)
        }
    }

    const createConversation = async (subject: string, body: string) => {
        try {
            const res = await axiosInstance.post<TenantMessage>(
                'tenant-message/inbox/new/',
                { subject, body },
            )
            setMessages((prev) => [res.data, ...prev])
            return res.data
        } catch (e) {
            toast.error('Error creating message')
            return Promise.reject(e)
        }
    }

    return {
        isLoading,
        isLoadingMore,
        messages,
        unreadCount,
        hasMore,
        replies,
        repliesLoading,
        getMessages,
        loadMore,
        getUnreadCount,
        getMessage,
        markRead,
        getReplies,
        sendReply,
        createConversation,
    }
}
